import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ADVANCE_DELAY_MS = 280;

export default function Assessment() {
  const [items, setItems] = useState([]);
  const [scale, setScale] = useState([]);
  const [responses, setResponses] = useState({}); // {item_id: 1..5}
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const advancingRef = useRef(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assessment/items");
        setItems(data.items || []);
        setScale(data.scale || []);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load items.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const total = items.length;
  const current = items[idx];
  const answered = current ? responses[current.id] : undefined;
  const progressPct = useMemo(
    () => (total ? Math.round(((idx + (answered ? 1 : 0)) / total) * 100) : 0),
    [idx, answered, total]
  );

  const select = (val) => {
    if (advancingRef.current || submitting) return;

    const isRevision = current.id in responses;   // user is editing a previous answer
    setResponses((r) => ({ ...r, [current.id]: val }));

    // Drop focus so a re-mounted next question doesn't inherit the focus ring.
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Revisions never auto-advance — the user is deliberately fixing data and
    // should stay where they are. They can use Back / Forward to navigate.
    if (isRevision) return;

    // First-time answer: auto-advance to the next *unanswered* question if any.
    // We never auto-submit; once everything is answered, the user gets a
    // Submit button so they can review before sending.
    if (idx < total - 1) {
      advancingRef.current = true;
      window.setTimeout(() => {
        setIdx((i) => Math.min(i + 1, total - 1));
        advancingRef.current = false;
      }, ADVANCE_DELAY_MS);
    }
  };

  const back = () => {
    if (submitting) return;
    if (idx > 0) setIdx(idx - 1);
  };

  const forward = () => {
    if (submitting) return;
    if (idx < total - 1) setIdx(idx + 1);
  };

  const submit = async (finalResponses) => {
    const r = finalResponses || responses;
    if (Object.keys(r).length !== total) {
      toast.error("Please answer every question before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {};
      Object.entries(r).forEach(([k, v]) => { payload[String(k)] = v; });
      const { data } = await api.post("/assessment/submit", { responses: payload });
      toast.success("Assessment submitted.");
      nav(`/results/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-clinical">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 flex items-center gap-3 text-slate-500" data-testid="assessment-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading assessment…
        </div>
      </div>
    );
  }

  if (!current) return null;

  const allAnswered = Object.keys(responses).length === total;

  return (
    <div className="min-h-screen bg-clinical" data-testid="assessment-page">
      <Header />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Progress */}
        <div className="mb-8" data-testid="assessment-progress">
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
              Question {idx + 1} of {total}
            </p>
            <p className="text-xs text-slate-500">{progressPct}%</p>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
              data-testid="progress-fill"
            />
          </div>
        </div>

        {/* Question card */}
        <Card key={current.id} className="border-slate-200 shadow-sm animate-fade-in-up">
          <CardContent className="p-6 sm:p-10">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">
              Indicate how much you agree
            </p>
            <h2
              className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 leading-snug"
              data-testid="assessment-question-text"
            >
              {current.text}
            </h2>

            {/* Likert */}
            <div
              className="mt-10 grid grid-cols-1 sm:grid-cols-5 gap-3"
              role="radiogroup"
              aria-label="Likert scale response"
            >
              {scale.map((opt) => {
                const selected = responses[current.id] === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => select(opt.value)}
                    className={[
                      "likert-option flex flex-col items-center justify-center text-center px-4 py-5 rounded-xl border-2 transition-all duration-200 cursor-pointer",
                      selected
                        ? "border-primary bg-accent text-primary shadow-sm"
                        : "border-slate-200 bg-white text-slate-700 hover:border-primary/40 hover:bg-accent/40",
                    ].join(" ")}
                    role="radio"
                    aria-checked={selected}
                    data-testid={`likert-option-${opt.value}`}
                  >
                    <span className="font-heading text-2xl font-semibold">{opt.value}</span>
                    <span className="mt-1 text-xs sm:text-sm leading-snug">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Nav */}
        <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={back}
              disabled={idx === 0 || submitting}
              data-testid="assessment-back-button"
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <Button
              variant="outline"
              onClick={forward}
              disabled={idx >= total - 1 || !(current.id in responses) || submitting}
              data-testid="assessment-forward-button"
              className="gap-2"
              title={!(current.id in responses) ? "Answer this question to move forward" : "Next question"}
            >
              Forward <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {submitting ? (
            <div className="inline-flex items-center gap-2 text-sm text-primary font-medium" data-testid="assessment-submitting">
              <Loader2 className="h-4 w-4 animate-spin" /> Submitting your assessment…
            </div>
          ) : allAnswered ? (
            <Button
              onClick={() => submit()}
              className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
              data-testid="submit-assessment-button"
            >
              <CheckCircle2 className="h-4 w-4" />
              Submit assessment
            </Button>
          ) : (
            <p className="text-xs text-slate-500 max-w-[18rem] text-right" data-testid="assessment-autoadvance-hint">
              Selecting an answer advances automatically. Use Back to revisit and fix any earlier responses.
            </p>
          )}
        </div>

        <p className="mt-6 text-xs text-slate-500 text-center">
          {Object.keys(responses).length} of {total} answered.
          {" "}
          {allAnswered
            ? "Review using Back / Forward, then press Submit when you're done."
            : "You can use Back to change any previous response."}
        </p>
      </main>
    </div>
  );
}
